import {Map, List, Record, type RecordOf} from 'immutable';
import {
  ActionTypeConsts,
  QuickEffectIds,
} from '@packages/systems/ix2/shared-constants';
import {Qualified} from '@packages/systems/wfdl-Qualified';

export const LIMITED_AFFECTED_ELEMENTS = {
  CHILDREN: 'CHILDREN',
  SIBLINGS: 'SIBLINGS',
  IMMEDIATE_CHILDREN: 'IMMEDIATE_CHILDREN',
} as const;

export type IX2SelectorData = Record.Factory<{
  selectorGuids: List<string> | string[];
}>;

export type IX2AffectedElementData = {
  selector: IX2SelectorData | null | undefined;
  limitAffectedElements:
    | keyof typeof LIMITED_AFFECTED_ELEMENTS
    | null
    | undefined;
};

// ActionConfigData + Specific data related to type
export type ActionConfigData = {
  delay: number;
  easing: string;
  duration: number;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ActionListId = string;

export type StartActionListConfigData = {
  actionListId: ActionListId | null | undefined; // the action list id;
  affectedElements: Map<string, IX2AffectedElementData>; // List of affected elements and their override configurations;
  autoStopEventId: string | null;
  playInReverse: boolean;
  delay: number;
  easing: string;
  duration: number;
};

export type StopActionListConfigData = {
  actionListId: ActionListId;
  freezePlayback: boolean;
  delay: number;
  easing: string;
  duration: number;
};

export type ContinuousActionListConfigData = {
  actionListId: ActionListId;
  affectedElements: Map<string, IX2AffectedElementData>;
};

export type IX2ActionItemSharedType = {
  id: string;
  actionTypeId: keyof typeof ActionTypeConsts | keyof typeof QuickEffectIds;
  instant: boolean;
};

export type IX2ActionListType = {
  id: string;
  title: string;
  createdOn: string;
  origin?: Qualified<string>;
};

export type IX2ActionConfigType =
  | Vec3PixelOrPercentConfigData
  | Vec3DegreeConfigData
  | TargetConfigData
  | UnitValueConfigData
  | LoopConfigData
  | ScaleConfigData;

export type IX2ActionItemType = IX2ActionItemSharedType & {
  config: IX2ActionConfigType;
};

export type IX2ActionItemGroupType = {
  actionItems: List<IX2ActionItemType>;
};

export type IX2TimedActionListType = IX2ActionListType & {
  actionItemGroups: List<IX2ActionItemGroupType>;
  useFirstGroupAsInitialState: boolean;
};

export const PARAMETER_TYPES = {
  SCROLL_PROGRESS: 'SCROLL_PROGRESS',
  MOUSE_X: 'MOUSE_X',
  MOUSE_Y: 'MOUSE_Y',
} as const;

export type IX2ContinuousActionGroupType = IX2ActionItemGroupType & {
  keyframe: number; // for continuous lists;
};

export type IX2ContinuousParameterGroupType = {
  id: string; // for "connected" events;
  type: IX2ContinuousParameterType; // Type of parameter;
  parameterLabel: string; // parameter title;
  continuousActionGroups: List<IX2ContinuousActionGroupType>;
};

export type IX2ContinuousParameterType = keyof typeof PARAMETER_TYPES;

/**
TODO: After removing Immutable, `continuousParameterGroups` would be more accurately
typed as the following:

type ContinuousParameterGroupsType =
  | [
      {
        id: string,
        type: $ElementType<IX2ContinuousParameterType, 'MOUSE_X'>,
        parameterLabel: string,
        continuousActionGroups: Array<IX2ContinuousActionGroupType>,
      },
      {
        id: string,
        type: $ElementType<IX2ContinuousParameterType, 'MOUSE_Y'>,
        parameterLabel: string,
        continuousActionGroups: Array<IX2ContinuousActionGroupType>,
      },
    ]
  | [
      {
        id: string,
        type: $ElementType<IX2ContinuousParameterType, 'SCROLL_PROGRESS'>,
        parameterLabel: string,
        continuousActionGroups: Array<IX2ContinuousActionGroupType>,
      },
    ];
 */
export type IX2ContinuousActionListType = IX2ActionListType & {
  continuousParameterGroups: List<IX2ContinuousParameterGroupType>;
};

export type Percent = '%';
export type Pixel = 'PX';
export type Degree = 'DEG';
export type PixelOrPercent = Percent | Pixel;
export type DisplaySetting =
  | 'block'
  | 'inline-block'
  | 'inline'
  | 'flex'
  | 'grid'
  | 'none';

export type IX2ActionItemTargetData = {
  pluginElement: string | null;
  objectId: string | null;
  appliesTo: 'ELEMENT' | 'ELEMENT_CLASS' | 'TRIGGER_ELEMENT';
  nodeId: string | null;
  styleBlockIds: null | List<string>;
  useEventTarget: null | boolean | 'CHILDREN' | 'SIBLINGS' | 'PARENT';
  boundaryMode: null | boolean;
};

export const IX2ActionItemTarget = Record<IX2ActionItemTargetData>(
  {
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'string | null'.
    pluginElement: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'string | null'.
    objectId: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type '"ELEMENT" | "ELEMENT_CLASS" | "TRIGGER_ELEMENT"'.
    appliesTo: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'string | null'.
    nodeId: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'null | List<string>'.
    styleBlockIds: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'null | boolean | "CHILDREN" | "SIBLINGS" | "PARENT"'.
    useEventTarget: undefined,
    // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'null | boolean'.
    boundaryMode: undefined,
  },
  'IX2ActionItemTarget'
);

export type IX2ActionItemBaseConfig = {
  target?: RecordOf<IX2ActionItemTargetData>;
};

// @ts-expect-error - TS2314 - Generic type 'Record<TProps>' requires 1 type argument(s).
export type TargetConfigData = Record<any, any> & IX2ActionItemBaseConfig;

export type Vec3PixelOrPercentConfigData = {
  xValue: number;
  yValue: number;
  zValue: number;
  xUnit: PixelOrPercent;
  yUnit: PixelOrPercent;
  zUnit: PixelOrPercent;
} & IX2ActionItemBaseConfig;

// For Rotation and Skew
export type Vec3DegreeConfigData = {
  xValue: number;
  yValue: number;
  zValue: number;
  xUnit: Degree;
  yUnit: Degree;
  zUnit: Degree;
} & IX2ActionItemBaseConfig;

export type UnitValueConfigData = {
  value: number;
  unit: Percent;
} & IX2ActionItemBaseConfig;

export type LoopConfigData = {
  value: number;
  isInfinite: boolean;
} & IX2ActionItemBaseConfig;

export type ScaleConfigData = {
  xValue: number;
  yValue: number;
  zValue: number;
  // TODO: locked is a flow editor concern only and should be moved out of this type
  locked: boolean;
} & IX2ActionItemBaseConfig;

export type SizeConfigData = {
  widthValue: number | null | undefined;
  heightValue: number | null | undefined;
  widthUnit: string;
  heightUnit: string;
  locked: boolean;
} & IX2ActionItemBaseConfig;

export type HideShowConfigData = {
  value: DisplaySetting | null | undefined;
} & IX2ActionItemBaseConfig;
